/**
 * Search Select Component.
 *
 * A reusable autocomplete/combobox that replaces plain `<select>` elements
 * throughout the application. Supports:
 *
 * - Typeahead search filtering with debounce
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Custom render for each option (icon, subtitle, badge)
 * - Async data loading with loading/empty states
 * - Clear button to reset selection
 * - Works as both controlled and search-on-type
 */

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search, X, ChevronDown, Check } from "lucide-react";

export interface SearchSelectOption {
  value: string;
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SearchSelectProps {
  /** Available options (can be updated dynamically for async search). */
  options: SearchSelectOption[];
  /** Currently selected value. */
  value: string;
  /** Called when the user selects an option. */
  onChange: (value: string) => void;
  /** Placeholder text when nothing is selected. */
  placeholder?: string;
  /** Whether the component is loading options. */
  isLoading?: boolean;
  /** Whether the component is disabled. */
  disabled?: boolean;
  /** Allow clearing the selection. */
  clearable?: boolean;
  /** Called when the search text changes (for async search). */
  onSearch?: (query: string) => void;
  /** Label text above the input. */
  label?: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Additional class name. */
  className?: string;
}

function SearchSelect({
  options,
  value,
  onChange,
  placeholder = "Search...",
  isLoading = false,
  disabled = false,
  clearable = true,
  onSearch,
  label,
  required = false,
  className,
}: SearchSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Find the selected option for display.
  const selectedOption = options.find((o) => o.value === value);

  // Filter options by query.
  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(q)),
    );
  }, [options, query]);

  useEffect(() => setHighlightIdx(0), [filteredOptions.length]);

  // Notify parent of search changes.
  useEffect(() => {
    onSearch?.(query);
  }, [query, onSearch]);

  const handleSelect = useCallback(
    (optValue: string) => {
      onChange(optValue);
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filteredOptions[highlightIdx];
      if (item && !item.disabled) handleSelect(item.value);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
    }
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setIsOpen(true);
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setQuery("");
    }, 200);
  };

  return (
    <div className={className} ref={containerRef}>
      {label && (
        <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
          {label}
          {required && (
            <span className="ml-0.5 text-[var(--text-danger)]">*</span>
          )}
        </label>
      )}

      <div className="relative">
        {/* Input / Display */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-tertiary)] pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={isOpen ? query : (selectedOption?.label ?? "")}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={selectedOption ? selectedOption.label : placeholder}
            disabled={disabled}
            className="w-full h-[38px] pl-9 pr-16 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)] focus-visible:border-[var(--border-focus)] transition-all duration-200 ease-[var(--ease-micro)] disabled:opacity-50 disabled:pointer-events-none"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {clearable && value && !disabled && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange("");
                  setQuery("");
                  inputRef.current?.focus();
                }}
                className="p-0.5 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <ChevronDown
              className={`h-3.5 w-3.5 text-[var(--text-tertiary)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--surface-base)] shadow-[var(--shadow-lg)] animate-scale-in">
            {isLoading && filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-[var(--text-tertiary)]">
                <span className="inline-block h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-1.5 align-middle" />
                Loading options...
              </div>
            )}

            {!isLoading && filteredOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-[var(--text-tertiary)]">
                {query ? `No results for "${query}"` : "No options available"}
              </div>
            )}

            {filteredOptions.map((option, idx) => (
              <div
                key={option.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (!option.disabled) handleSelect(option.value);
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-[var(--color-border)] last:border-0 transition-colors ${
                  option.disabled
                    ? "opacity-40 cursor-not-allowed"
                    : idx === highlightIdx
                      ? "bg-[var(--color-interactive-primary)]/[0.06]"
                      : "hover:bg-[var(--surface-sunken)]"
                }`}
              >
                {option.icon && <div className="shrink-0">{option.icon}</div>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-primary)] truncate">
                    {option.label}
                  </p>
                  {option.sublabel && (
                    <p className="text-[11px] text-[var(--text-tertiary)] truncate">
                      {option.sublabel}
                    </p>
                  )}
                </div>
                {option.value === value && (
                  <Check className="h-4 w-4 text-[var(--color-interactive-primary)] shrink-0" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchSelect;
